import React from 'react';
import { PropTypes } from 'prop-types';
import _ from 'underscore';
import SmoothScroll from 'smooth-scroll';

// For inserting dynamic styles
// Appends the children of the component (which must be a string of CSS) to the head element
// Use es6 multi-line strings (``) to maintain your sanity
export class Style extends React.Component {
    constructor() {
        super();
        const styleNode = document.createElement('style');
        styleNode.type = 'text/css';
        this._styleNode = styleNode;
    }

    componentDidMount() {
        const head = document.getElementsByTagName('head')[0];
        head.appendChild(this._styleNode);
    }

    render() {
        this._styleNode.innerHTML = this.props.children;
        return null;
    }
}

Style.propTypes = {
    children: PropTypes.string.isRequired
};

// tag that removes indentation and newlines from template strings
export const dedent = (strs, ...args) => {
    return _.flatten(_.zip(strs.map(s => s.replace(/\s\s+/g, ' ')), args)).join('');
};

export const makeDesignationString = function (designationsArray, dictionary, additionalDesignationsArray) {
    const output = [];
    const allDesignations = [...(designationsArray || []), ...(additionalDesignationsArray || [])];

    _.each(allDesignations, function (designation) {
        const designationValue = designation && dictionary[designation];
        if (designationValue) {
            output.push(designationValue.replace(/(®)/ig, "<sup>$1</sup>"));
        }
    });
    return output.join(", ");
};

export const makeStateLicensesString = (licensesArray, memberData) => {
    const STATES_REQUIRING_LICENSE_NUMBERS = ["CA", "AR"];
    return licensesArray
        .map((license) => {
            const requiresLicenseNumber = _.contains(STATES_REQUIRING_LICENSE_NUMBERS, license);
            if (requiresLicenseNumber && memberData["state_license_number_" + license]) {
                return license + (" Insurance Lic. #" + memberData["state_license_number_" + license] || "");
            }
            return license;
        })
        .join(", ");
};

export const isAbsoluteLink = function (url) {
    const absoluteCheck = /^(https?|ftp):\/\/[^\s\/$.?#].[^\s]*$/;
    return absoluteCheck.test(url);
};

export const formatAbsoluteLink = (baseLink, url) => `${baseLink}${url}`;

/**
 * Takes newData and inserts it into array with oldData if it exists
 * @param {string|string[]} newData
 * @param {array} [oldData]
 */
export const formatCheckboxData = (newData, oldData) => {
    if (_.isArray(oldData)) {
        const updatedData = _.without(oldData, newData);
        if (updatedData.length === oldData.length) {
            return [...oldData, newData];
        }
        return updatedData;
    }
    else {
        return [newData];
    }
};

export const emailPatternValidation = function (email) {
    /* eslint-disable max-len */
    const pattern = /^(([^<>()\[\]\.,;:\s@\"]+(\.[^<>()\[\]\.,;:\s@\"]+)*)|(\".+\"))@(([^<>()[\]\.,;:\s@\"]+\.)+[^<>()[\]\.,;:\s@\"]{2,})$/;
    /* eslint-enable max-len */
    return pattern.test(email);
};

/**
 * Remove a hash from the URL via replaceState if supported
 * @returns {undefined}
*/
export const removeHash = function () {
    const path = window.location.pathname,
        search = window.location.search;

    if ("replaceState" in window.history) {
        window.history.replaceState('', document.title, path + search);
    }
};

/**
 * Scroll from page top to the element in question.
 * Caller function should first verify that a URL hash and the element+id exists in the page
 * @param {string} target - URL hash
 * @returns {undefined}
 */
export const scrollToHash = function (hash) {
    const speed = 500;
    const scroll = new SmoothScroll();
    const scrollToY = getElementOffsetTop(document.querySelector(hash)) - getNavbarHeight();

    scroll.animateScroll(scrollToY, null, { speed });
};

/* i don't like having these here at all, i want to have a sepearate helpers file for just the scroll stuff,
but don't wanna do too much during one round of refactor */
export const getElementOffsetTop = function (element) {
    return element.getBoundingClientRect().top + document.documentElement.scrollTop;
};

export const getNavbarHeight = function () {
    const navbar = document.querySelector('.navbar-section') || {};
    return navbar.clientHeight || 0;
};

/**
 * When the Home Component mounts, check for a URL hash, remove it, and initiate scroll on load
 * Example: /#contact-form to smooth scroll to the contact-form position
 * Note: document.querySelector could throw a DOMException if selector is not valid
 * @returns {undefined}
 */
export const handleHomePageComponentMount = function () {
    const hash = window.location.hash;

    if (hash && Boolean(document.querySelector(hash))) {
        removeHash();

        window.addEventListener('load', function load() {
            window.removeEventListener("load", load, false);
            scrollToHash(hash);
        });
    }
};

/*
    * Checks empty_state_text for text
*/
export const checkEmptyState = function (domString) {
    const templateHtml = document.createElement('div');
    templateHtml.innerHTML = domString;
    return templateHtml.textContent.length ? domString : '';
};

/**
 * Extracts the footer sections from theme_data and profile and merges them into a single array
 * @param {Object} pageData page data object with profile and theme_data properties
 * @param {Object} pageData.profile
 * @param {Object} pageData.theme_data
 * @returns {String[]} Array of content for the footer. Either plain string or HTML string.
 */
export const makeFooterSections = ({ profile = {}, theme_data = { footer: {} } } = {}) => {
    const orgFooterSections = theme_data.footer.footer_sections || [];
    const siteFooterSections = profile.disclaimer || [];
    // Content is required so filter out any sections that don't have content
    return [].concat(orgFooterSections, siteFooterSections).filter(section => section.content);
};

export const getHeroBackgroundPicture = (
    agentsSelection,
    adminsDefault,
    adminsImageList
) => {
    const isAgentsSelectionFromDropdown = Array.isArray(agentsSelection);
    // This if is needed for backward compatibility
    if (!isAgentsSelectionFromDropdown) {
        return agentsSelection || adminsDefault;
    }
    const selectedImageName = agentsSelection[0];
    // This is needed because you cannot unselect a dropdown fanmanagement
    const isEmptyDropdownValueSelected = selectedImageName === 'no-item';
    const { imageUrl: selectedImageUrl } = adminsImageList
        .find(({ imageName }) => selectedImageName === imageName) || {};
    return isEmptyDropdownValueSelected
        ? adminsDefault
        : (selectedImageUrl || adminsDefault);
};

export const addQueryParam = (url, key, value) => {
    const connector = url.indexOf("?") === -1 ? '?' : '&';
    return url + connector + key + '=' + value;
};


/**
 * Create cookie
 * @param name - string
 * @param value - string
 * @param [maxAge] - seconds
 */
export const createCookie = (name, value, maxAge) => {
    let expires = "";
    if (maxAge) {
        const d = new Date();
        const date = new Date(d.getTime() + (maxAge * 1000));
        expires = "; expires=" + date.toGMTString();
    }
    document.cookie = name + "=" + value + expires + "; path=/";
};

/**
   * Read cookie
   * @param name - string
   */
export const readCookie = (name) => {
    const nameEQ = name + "=";
    const ca = document.cookie.split(';');
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') { c = c.substring(1, c.length); }
        if (c.indexOf(nameEQ) === 0) { return c.substring(nameEQ.length, c.length); }
    }
    return null;
};

/**
   * Remove cookie
   * @param name - string
   */
export const eraseCookie = (name) => {
    if (readCookie(name)) {
        createCookie(name, "", -1);
    }
};

export const setAriaLabelAttributeForExternalLinks = (richTextElement, externalWindowLinkAriaText) => {
    if (!externalWindowLinkAriaText) {
        return richTextElement;
    }

    const matches = [];
    const regexp = RegExp(/<a[\s]+([^>]+)>((?:.(?!\<\/a\>))*.)<\/a>/gm);
    let tmp;
    while ((tmp = regexp.exec(richTextElement)) !== null) {
        matches.push(tmp);
    }

    if (!matches.length) {
        return richTextElement;
    }

    for (let i = 0; i < matches.length; i++) {
        const match = matches[i];
        const originalAnchorElement = match[0];
        let newAnchorElement = match[0];
        const attributes = match[1];
        const ariaLabelText = match[2].replace(/(<([^>]+)>)/gi, "");

        if (attributes.includes('target="_blank"') && !attributes.includes('aria-label="')) {
            newAnchorElement = newAnchorElement.replace(
                '<a ', `<a aria-label="${ariaLabelText} (${externalWindowLinkAriaText})"`);
        }

        richTextElement = richTextElement.replace(originalAnchorElement, newAnchorElement);
    }

    return richTextElement;
};



// WEBPACK FOOTER //
// ./components/helpers.jsx